<template>
	<!-- TODO: update with aria roles when those are implemented on inputs https://logaretm.github.io/vee-validate/guide/a11y.html -->
	<ValidationProvider
		v-slot="{ errors, invalid, failedRules }"
		:debounce="100"
		:rules="rules"
		:name="name"
		:mode="validationMode"
		:vid="vid"
		tag="div"
		class="validated-input"
		:immediate="immediate"
	>
		<c-checkbox
			v-if="isCheckbox"
			:id="vid"
			v-model="innerValue"
			v-bind="$attrs"
			:error="errors[0]"
			:class="{ 'input--error': errors[0] }"
		>
			<slot></slot>
		</c-checkbox>

		<c-input
			v-else
			:id="vid"
			v-model="innerValue"
			v-bind="$attrs"
			:type="type"
			:show-label="showLabel"
			:name="name"
			:label="label"
			:class="[{ 'input--error': errors[0] }, {'input--has-help-text': !!$scopedSlots['help-text']}]"
			@focus="$emit('focus')"
			@blur="$emit('blur', $event)"
			@change="$emit('change', $event)"
		>
			<template #help-text><slot name="help-text" :failed-rules="failedRules" :errors="errors" :invalid="invalid" /></template>
			<template v-if="showSuffixIcon" #suffix>
				<a :href="suffixLink" class="flex" target="_blank">
					<Inline :svg="suffixIcon" class="deep" />
				</a>
			</template>
		</c-input>

		<transition name="el-zoom-in-top">
			<slot name="error" :failed-rules="failedRules" :errors="errors" :invalid="invalid">
				<c-callout v-if="errors[0]" type="error" size="s" :attached="isErrorAttached" :class="{'mt-2': !isErrorAttached}">
					{{ errors[0] }}
				</c-callout>
			</slot>
		</transition>
	</ValidationProvider>
</template>

<script lang="ts">
	// Wrap form component with `ValidationObserver` to communicate with the form if all the inputs are valid: https://logaretm.github.io/vee-validate/guide/validation-observer.html#validation-observer
	import { ValidationProvider } from 'vee-validate';
	import CCallout from '../content/Callout.vue';
	import CCheckbox from './Checkbox.vue';
	import CInput, { type InputType } from './Input.vue';
	import './ValidationTypes';
	import { defineComponent, ref, type PropType } from 'vue';

	export type ValidatedInputType = InputType | 'checkbox';

	export default defineComponent({
		name: 'CValidatedInput',
		components: {
			ValidationProvider,
			CCallout,
			CCheckbox,
			CInput
		},
		inheritAttrs: false,
		props: {
			label: { type: String, default: '' },
			rules: { type: [String, Object], required: true },
			value: { type: [String, Boolean], required: true },
			name: { type: String, default: '' },
			type: { type: String as PropType<ValidatedInputType>, default: 'text' },
			validationMode: { type: String, default: 'eager' },
			vid: { type: String, default: '' },
			immediate: { type: Boolean, default: false },
			showLabel: { type: Boolean, default: false },
			validationIsBox: { type: Boolean, default: false },
			showSuffixIcon: { type: Boolean, default: false },
			suffixIcon: { type: String, default: 'external-link' },
			suffixLink: { type: String, default: '/' }
		},
		emits: ['input', 'focus', 'blur', 'change'],
		setup(props) {
			return {
				innerValue: ref(props.value)
			};
		},
		computed: {
			isCheckbox() {
				return this.type === 'checkbox';
			},
			isErrorAttached() {
				return !this.isCheckbox && !this.validationIsBox && !this.$scopedSlots['help-text'];
			}
		},
		watch: {
			innerValue(val) {
				this.$emit('input', val);
			},
			value(val) {
				this.innerValue = val;
			}
		}
	});
</script>
<style lang="scss">
.input--error {
	padding-bottom: 0;
}

.input--error .el-input__inner {
	border-color: $stroke__error;
}

.input--error:not(.input--has-help-text) .el-input__inner {
	border-bottom: 0;
}

.input__error {
	color: $text__error;
}

.validated-input {

	.el-input__suffix {
		pointer-events: initial;
	}
}

</style>